import * as React from 'react';
import {graphql} from 'react-relay';

/** Quick-filter sentinel meaning "assigned to the current user". Resolved to the
 *  Forecast person id before filters reach the backend (ACC-436). */
export const ME_SENTINEL = '__me__';

/**
 * Resolve the {@link ME_SENTINEL} to the current Forecast person id. The backend
 * has no notion of "me", so this must happen client-side. When the current user
 * id is unknown (standalone dev), the sentinel is dropped rather than sent raw.
 */
export function resolveMeAssignee(assigneeIds: string[], currentUserId: string | undefined): string[] {
	if (!currentUserId) return assigneeIds.filter(id => id !== ME_SENTINEL);
	return assigneeIds.map(id => (id === ME_SENTINEL ? currentUserId : id));
}

export const TicketListQuery = graphql`
	query useTicketListQueryQuery($first: Int, $after: String, $filters: TicketFilters) {
		viewer {
			actualPersonId
			...useTicketListQuery_tickets @arguments(first: $first, after: $after, filters: $filters)
		}
	}
`;

export const TicketListPaginationFragment = graphql`
	fragment useTicketListQuery_tickets on Viewer
	@refetchable(queryName: "useTicketListQueryPaginationQuery")
	@argumentDefinitions(first: {type: "Int", defaultValue: 25}, after: {type: "String"}, filters: {type: "TicketFilters"}) {
		tickets(first: $first, after: $after, filters: $filters) @connection(key: "TicketList_tickets") {
			edges {
				cursor
				node {
					id
					subject
					status
					priority
					channel
					requesterId
					assigneeId
					createdAt
					updatedAt
					closedAt
					slug
					requester {
						id
						name
						email
					}
					assignee {
						fullName
						...ticketingPerson_person
					}
					labels {
						id
						name
						color
					}
					sla {
						breached
						elapsedSeconds
						thresholdSeconds
						paused
					}
					...TicketRow_ticket
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

export interface TicketFiltersInput {
	status: string[];
	priority: string[];
	sla_status: string[];
	assignee_id: string[];
	label_id: string[];
	requester_id: string[];
	created_after: string;
	created_before: string;
	updated_after: string;
	updated_before: string;
}

export function buildTicketFilters(
	filters: TicketFiltersInput,
	sort: string | undefined,
	order: string | undefined,
	search: string | undefined
): Record<string, unknown> | undefined {
	const result: Record<string, unknown> = {};
	if (filters.status.length > 0) result.status = filters.status;
	if (filters.priority.length > 0) result.priority = filters.priority;
	if (filters.sla_status.length > 0) result.slaStatus = filters.sla_status;
	if (filters.assignee_id.length > 0) result.assigneeId = filters.assignee_id;
	if (filters.label_id.length > 0) result.labelId = filters.label_id;
	if (filters.requester_id.length > 0) result.requesterId = filters.requester_id;
	if (filters.created_after) result.createdAfter = filters.created_after;
	if (filters.created_before) result.createdBefore = filters.created_before;
	if (filters.updated_after) result.updatedAfter = filters.updated_after;
	if (filters.updated_before) result.updatedBefore = filters.updated_before;
	if (sort) result.sort = sort;
	if (order) result.order = order;
	if (search && search.trim().length > 0) result.search = search.trim();
	return Object.keys(result).length > 0 ? result : undefined;
}

export interface TicketListVariablesOptions {
	first?: number;
	filters: TicketFiltersInput;
	sort?: string;
	order?: string;
	search?: string;
}

export interface TicketListQueryVariables {
	first: number;
	after: string | null;
	filters: Record<string, unknown> | null;
}

/**
 * Builds the variables passed to {@link TicketListQuery} via
 * `ForecastQueryRenderer`. The query boundary now lives in the route
 * (ACC-460); this helper keeps the filter/first derivation it used to own.
 */
export function buildTicketListVariables(options: TicketListVariablesOptions): TicketListQueryVariables {
	const {first = 25, filters, sort, order, search} = options;
	const relayFilters = buildTicketFilters(filters, sort, order, search);
	return {
		first,
		after: null,
		filters: relayFilters ?? null,
	};
}

export function useTicketListFetchKey() {
	const [fetchKey, setFetchKey] = React.useState(0);
	const refetch = React.useCallback(() => setFetchKey(k => k + 1), []);
	return {fetchKey, refetch};
}
